import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { taskAPI, adminSystemAPI } from '../../utils/crmApi';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const TaskTemplateForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isEdit = !!id;
    const isGlobalTemplate = searchParams.get('global') === 'true';

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'immigration',
        priority: 'medium',
        estimatedHours: 0,
        steps: [{ title: '', description: '', status: 'pending', order: 0 }],
        tags: [],
        isActive: true
    });

    const categories = [
        { value: 'immigration', label: 'Immigration' },
        { value: 'relocation', label: 'Relocation' },
        { value: 'documentation', label: 'Documentation' },
        { value: 'legal', label: 'Legal' },
        { value: 'financial', label: 'Financial' },
        { value: 'housing', label: 'Housing' },
        { value: 'education', label: 'Education' },
        { value: 'other', label: 'Other' }
    ];

    const priorities = [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' }
    ];

    useEffect(() => {
        if (isEdit) {
            loadTemplate();
        }
    }, [id]);

    const loadTemplate = async () => {
        try {
            setLoading(true);
            const response = await taskAPI.getTemplateById(id);
            if (response.data.success) {
                const template = response.data.data;
                setFormData({
                    title: template.title || '',
                    description: template.description || '',
                    category: template.category || 'immigration',
                    priority: template.priority || 'medium',
                    estimatedHours: template.estimatedHours || 0,
                    steps: template.steps?.length > 0 ? template.steps.map(step => ({
                        title: step.title || '',
                        description: step.description || '',
                        status: step.status || 'pending',
                        order: step.order || 0
                    })) : [{ title: '', description: '', status: 'pending', order: 0 }],
                    tags: template.tags || [],
                    isActive: template.isActive !== false
                });
            }
        } catch (error) {
            console.error('Failed to load template:', error);
            toast.error('Failed to load template');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleStepChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.map((step, i) => 
                i === index ? { ...step, [field]: value } : step
            )
        }));
    };

    const addStep = () => {
        setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, { title: '', description: '', status: 'pending', order: prev.steps.length }]
        }));
    };

    const removeStep = (index) => {
        if (formData.steps.length === 1) {
            toast.error('At least one step is required');
            return;
        }
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, order: i }))
        }));
    };

    const addTag = (tag) => {
        if (tag && !formData.tags.includes(tag)) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tag]
            }));
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }

        if (formData.steps.some(step => !step.title.trim())) {
            toast.error('All steps must have titles');
            return;
        }

        try {
            setLoading(true);
            
            if (isGlobalTemplate) {
                // Save as global template (task template = step-by-step process)
                const slug = formData.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                const globalTemplateData = {
                    type: 'task', // Task templates are step-by-step processes, not automation workflows
                    slug: slug,
                    title: formData.title,
                    description: formData.description || '',
                    category: formData.category || 'other',
                    tags: formData.tags || [],
                    content: {
                        estimatedDuration: formData.estimatedHours ? `${formData.estimatedHours} hours` : '',
                        steps: formData.steps.map((step, index) => ({
                            title: step.title,
                            description: step.description || '',
                            order: step.order ?? index + 1,
                            estimatedDuration: '',
                            requiredDocuments: [],
                            links: []
                        }))
                    }
                };
                
                if (isEdit) {
                    await adminSystemAPI.updateTemplate(id, globalTemplateData);
                    toast.success('Global template updated successfully!');
                } else {
                    await adminSystemAPI.createTemplate(globalTemplateData);
                    toast.success('Global template created successfully!');
                }
                
                // Close window if opened from admin dashboard
                if (window.opener) {
                    window.close();
                } else {
                    navigate('/admin?tab=templates');
                }
            } else {
                // Save as regular agency template
                if (isEdit) {
                    await taskAPI.updateTemplate(id, formData);
                    toast.success('Template updated successfully!');
                } else {
                    await taskAPI.createTemplate(formData);
                    toast.success('Template created successfully!');
                }
                
                navigate('/tasks');
            }
        } catch (error) {
            console.error('Failed to save template:', error);
            toast.error(error.response?.data?.message || 'Failed to save template');
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEdit) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate('/tasks')}
                        className="p-2 text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">
                            {isGlobalTemplate 
                                ? (isEdit ? 'Edit Global Template' : 'Create Global Template')
                                : (isEdit ? 'Edit Task Template' : 'Create Task Template')
                            }
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {isGlobalTemplate
                                ? (isEdit ? 'Update the global template details' : 'Create a global template that will be available to all agencies')
                                : (isEdit ? 'Update the task template details' : 'Create a reusable task template that can be assigned to multiple clients')
                            }
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Basic Information</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Template Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                placeholder="e.g., NIE Application Process"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Category
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            >
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={3}
                            className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            placeholder="Describe what this template is for and when it should be used..."
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Priority
                            </label>
                            <select
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            >
                                {priorities.map(priority => (
                                    <option key={priority.value} value={priority.value}>{priority.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Estimated Hours
                            </label>
                            <input
                                type="number"
                                name="estimatedHours"
                                value={formData.estimatedHours}
                                onChange={handleChange}
                                min="0"
                                step="0.5"
                                className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                placeholder="0"
                            />
                        </div>

                        <div className="flex items-center">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                />
                                <span className="ml-2 text-sm text-foreground">Active Template</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Tags */}
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Tags</h2>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                        {formData.tags.map((tag, index) => (
                            <span
                                key={index}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                            >
                                {tag}
                                <button
                                    type="button"
                                    onClick={() => removeTag(tag)}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    <TrashIcon className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>

                    <input
                        type="text"
                        placeholder="Add a tag and press Enter"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addTag(e.target.value.trim());
                                e.target.value = '';
                            }
                        }}
                        className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                    />
                </div>

                {/* Task Steps */}
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-foreground">Task Steps</h2>
                        <button
                            type="button"
                            onClick={addStep}
                            className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                        >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add Step
                        </button>
                    </div>

                    <div className="space-y-4">
                        {formData.steps.map((step, index) => (
                            <div key={index} className="flex items-start space-x-4 p-4 border border-border rounded-lg">
                                <div className="flex-shrink-0 mt-2">
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-muted text-muted-foreground text-sm font-medium rounded-full">
                                        {index + 1}
                                    </span>
                                </div>
                                
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        value={step.title}
                                        onChange={(e) => handleStepChange(index, 'title', e.target.value)}
                                        placeholder="Step title..."
                                        className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring mb-2"
                                        required
                                    />
                                    <textarea
                                        value={step.description}
                                        onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                                        placeholder="Describe this step..."
                                        className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        rows={2}
                                    />
                                </div>
                                
                                <div className="flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => removeStep(index)}
                                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg"
                                        disabled={formData.steps.length === 1}
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
                    <button
                        type="button"
                        onClick={() => navigate('/tasks')}
                        className="px-4 py-2 text-foreground bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? 'Saving...' : (isEdit ? 'Update Template' : 'Create Template')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TaskTemplateForm;




